import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import { useT } from "../i18n/LanguageProvider";
import {
  validateInviteToken,
  acceptOrgInvite,
  validateInstitutionInvite,
  acceptInstitutionInvite,
} from "../api/officers";
import { tokenStore } from "../api/tokenStore";
import { passwordProblem } from "../utils/passwordPolicy";
import { useIsMobile } from "../hooks/useIsMobile";
import { C, font, radii, shadows, space, type } from "../styles/tokens";
import { BrandButton, BrandCard } from "../components/brand";

/**
 * AcceptInvitePage - the invitee's entry point.
 * Follows BRAND_MANUAL.md (Sections 4, 5, 6, 7, 8):
 * - One password field with show/hide toggle; NO confirm password repeat.
 * - Centred brand mark in the header (Section 7).
 * - Brand typography, 48px height inputs, 8px radius, green pill button.
 */
export function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const isInstitutionInvite = searchParams.get("kind") === "institution";

  const t = useT();
  const { refreshLocalUser } = useAuth();
  const isMobile = useIsMobile();

  // Token-flow specific state
  const [tokenLoading, setTokenLoading] = useState(Boolean(token));
  const [tokenError, setTokenError] = useState(null);
  const [tokenData, setTokenData] = useState(null);

  // Common & Form states
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("form"); // "form" | "success"
  const [linkedTo, setLinkedTo] = useState(null);

  // Validate token on mount if present
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setTokenLoading(true);
    setTokenError(null);

    const validate = isInstitutionInvite
      ? validateInstitutionInvite
      : validateInviteToken;

    validate(token)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || res;
        setTokenData({
          ...data,
          orgName: data?.orgName ?? data?.institutionName,
        });
        if (data?.fullName) setFullName(data.fullName);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "This invitation link is invalid or has expired. Please ask your administrator to send a new invitation.";
        setTokenError(msg);
      })
      .finally(() => {
        if (!cancelled) setTokenLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, isInstitutionInvite]);

  // Token-based acceptance submit handler
  async function handleTokenSubmit(e) {
    e.preventDefault();
    setError(null);

    const passwordError = passwordProblem(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setSubmitting(true);
    try {
      const accept = isInstitutionInvite
        ? acceptInstitutionInvite
        : acceptOrgInvite;
      const res = await accept({
        token: token.trim(),
        password,
        fullName: fullName.trim() || undefined,
        phone: phone.trim() || undefined,
      });

      const responseData = res?.data || res;
      if (responseData?.accessToken) {
        tokenStore.setAccessToken(responseData.accessToken);
      }

      await refreshLocalUser();
      setLinkedTo(responseData);
      setStep("success");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not complete invitation setup. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Token loading view
  if (token && tokenLoading) {
    return (
      <div style={pageStyle}>
        <div style={{ width: "100%", maxWidth: 440, textAlign: "center" }}>
          <BrandHeader />
          <BrandCard style={{ padding: isMobile ? space.lg : space.xxl, textAlign: "center" }}>
            <div
              style={{
                display: "inline-block",
                width: 32,
                height: 32,
                border: `3px solid ${C.primaryTint}`,
                borderTopColor: C.primary,
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                marginBottom: space.md,
              }}
            />
            <p style={{ color: C.textMuted, fontSize: type.body.fontSize, margin: 0 }}>
              Validating your invitation...
            </p>
          </BrandCard>
        </div>
      </div>
    );
  }

  // Token error view
  if (token && tokenError) {
    return (
      <div style={pageStyle}>
        <div style={{ width: "100%", maxWidth: 440 }}>
          <BrandHeader />
          <BrandCard
            style={{
              textAlign: "center",
              padding: isMobile ? space.lg : space.xxl,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: radii.pill,
                backgroundColor: C.dangerTint,
                color: C.danger,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: `0 auto ${space.md}px`,
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h1
              style={{
                fontSize: type.title.fontSize,
                lineHeight: type.title.lineHeight,
                fontWeight: type.title.fontWeight,
                color: C.navy,
                margin: `0 0 ${space.sm}px`,
              }}
            >
              Invitation Link Invalid or Expired
            </h1>
            <p
              style={{
                fontSize: type.body.fontSize,
                lineHeight: type.body.lineHeight,
                color: C.textMuted,
                margin: `0 0 ${space.xl}px`,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {tokenError}
            </p>
            <BrandButton
              variant="secondary"
              fullWidth
              onClick={() => navigate("/")}
            >
              Back to Home
            </BrandButton>
          </BrandCard>
        </div>
      </div>
    );
  }

  // Success view (both flows)
  if (step === "success") {
    const orgName =
      linkedTo?.organisation?.name ??
      linkedTo?.institution?.name ??
      tokenData?.orgName ??
      "your organisation";
    const orgStatus =
      linkedTo?.organisation?.status ??
      linkedTo?.institution?.status ??
      "APPROVED";
    const nextRoute =
      orgStatus === "APPROVED" ? "/dashboard" : "/awaiting-approval";

    return (
      <div style={pageStyle}>
        <div style={{ width: "100%", maxWidth: 440 }}>
          <BrandHeader />
          <BrandCard
            style={{
              textAlign: "center",
              padding: isMobile ? space.lg : space.xxl,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: radii.pill,
                backgroundColor: C.successTint,
                color: C.success,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: `0 auto ${space.md}px`,
              }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1
              style={{
                fontSize: type.title.fontSize,
                lineHeight: type.title.lineHeight,
                fontWeight: type.title.fontWeight,
                color: C.navy,
                margin: `0 0 ${space.sm}px`,
              }}
            >
              You're in!
            </h1>
            <p
              style={{
                fontSize: type.body.fontSize,
                lineHeight: type.body.lineHeight,
                color: C.textMuted,
                margin: `0 0 ${space.xl}px`,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              You're now an officer at <strong>{orgName}</strong>.
              {orgStatus === "APPROVED"
                ? " Head to the dashboard to start reviewing requests."
                : " You'll see the dashboard once your organisation is approved by the platform team."}
            </p>
            <BrandButton
              variant="primary"
              fullWidth
              onClick={() => navigate(nextRoute, { replace: true })}
            >
              {orgStatus === "APPROVED" ? "Go to dashboard" : "Continue"}
            </BrandButton>
          </BrandCard>
        </div>
      </div>
    );
  }

  // Token-based form view
  if (token && tokenData) {
    return (
      <div style={pageStyle}>
        <div style={{ width: "100%", maxWidth: 440 }}>
          <BrandHeader />
          <BrandCard style={{ padding: isMobile ? space.lg : space.xxl }}>
            <div
              style={{
                display: "inline-block",
                backgroundColor: C.infoTint,
                color: C.navy,
                fontSize: type.overline.fontSize,
                fontWeight: type.overline.fontWeight,
                letterSpacing: type.overline.letterSpacing,
                textTransform: type.overline.textTransform,
                padding: "4px 10px",
                borderRadius: radii.control,
                marginBottom: space.sm,
              }}
            >
              Officer Invitation
            </div>

            <h1
              style={{
                fontSize: type.title.fontSize,
                lineHeight: type.title.lineHeight,
                fontWeight: type.title.fontWeight,
                color: C.navy,
                margin: `0 0 ${space.xs}px`,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              Join {tokenData.orgName}
            </h1>
            <p
              style={{
                fontSize: type.body.fontSize,
                lineHeight: type.body.lineHeight,
                color: C.textMuted,
                margin: `0 0 ${space.xl}px`,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              You've been invited as an officer
              {tokenData.designation ? ` (${tokenData.designation})` : ""}. Set
              up your login password to activate your account.
            </p>

            <form onSubmit={handleTokenSubmit}>
              {/* Email (Readonly) */}
              <div style={{ marginBottom: space.md }}>
                <label style={labelStyle}>Invited email</label>
                <input
                  type="email"
                  value={tokenData.email}
                  disabled
                  style={disabledInputStyle}
                />
                <div style={hintStyle}>
                  Your account is tied to this invited corporate email address.
                </div>
              </div>

              {/* Full Name */}
              <div style={{ marginBottom: space.md }}>
                <label style={labelStyle}>Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. Priya Sharma"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoComplete="name"
                  required
                  style={inputStyle}
                />
              </div>

              {/* Phone */}
              <div style={{ marginBottom: space.md }}>
                <label style={labelStyle}>Phone number (optional)</label>
                <input
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  style={inputStyle}
                />
              </div>

              {/* Single Password Field with Show/Hide Toggle (Section 8) */}
              <div style={{ marginBottom: space.lg }}>
                <label style={labelStyle}>Create a password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 8 characters, with A-Z, a-z, 0-9"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    required
                    style={{
                      ...inputStyle,
                      paddingRight: 44,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "transparent",
                      border: "none",
                      color: C.textSubtle,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    {showPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m3 3 18 18" />
                        <path d="M10.6 5.2A10.7 10.7 0 0 1 12 5c6 0 9.5 7 9.5 7a17.5 17.5 0 0 1-3.1 3.9" />
                        <path d="M6.2 6.3C3.9 8 2.5 12 2.5 12s3.5 7 9.5 7c1.5 0 2.8-.4 4-1" />
                        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6Z" />
                        <circle cx="12" cy="12" r="2.5" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  style={{
                    fontSize: type.caption.fontSize,
                    lineHeight: type.caption.lineHeight,
                    color: C.danger,
                    backgroundColor: C.dangerTint,
                    padding: `${space.sm}px ${space.md}px`,
                    borderRadius: radii.control,
                    marginBottom: space.md,
                  }}
                >
                  {error}
                </div>
              )}

              <BrandButton
                type="submit"
                variant="primary"
                fullWidth
                loading={submitting}
              >
                {submitting ? "Setting up your account..." : "Activate Account & Join"}
              </BrandButton>
            </form>

            <div
              style={{
                marginTop: space.lg,
                paddingTop: space.md,
                borderTop: `1px solid ${C.divider}`,
                fontSize: type.caption.fontSize,
                color: C.textMuted,
                textAlign: "center",
              }}
            >
              Not expecting an invitation?{" "}
              <Link
                to="/"
                style={{
                  color: C.primary,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Back to home
              </Link>
            </div>
          </BrandCard>
        </div>
      </div>
    );
  }

  // No token in the URL: invitations only work through the emailed link.
  return (
    <div style={pageStyle}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <BrandHeader />
        <BrandCard style={{ padding: isMobile ? space.lg : space.xxl }}>
          <h1
            style={{
              fontSize: type.title.fontSize,
              lineHeight: type.title.lineHeight,
              fontWeight: type.title.fontWeight,
              color: C.navy,
              margin: `0 0 ${space.xs}px`,
            }}
          >
            Open your invitation link
          </h1>
          <p
            style={{
              fontSize: type.body.fontSize,
              lineHeight: type.body.lineHeight,
              color: C.textMuted,
              margin: `0 0 ${space.xl}px`,
            }}
          >
            Officer invitations arrive by email. Open the link in that email to
            set your password. If the link has expired, ask your administrator
            to send a new one. Already set your password? Sign in instead.
          </p>

          <BrandButton
            variant="primary"
            fullWidth
            onClick={() => navigate("/login")}
          >
            Sign in
          </BrandButton>

          <div
            style={{
              marginTop: space.lg,
              paddingTop: space.md,
              borderTop: `1px solid ${C.divider}`,
              fontSize: type.caption.fontSize,
              color: C.textMuted,
              textAlign: "center",
            }}
          >
            Not an officer?{" "}
            <Link
              to="/"
              style={{ color: C.primary, fontWeight: 600, textDecoration: "none" }}
            >
              Back to home
            </Link>
          </div>
        </BrandCard>
      </div>
    </div>
  );
}

function BrandHeader() {
  return (
    <div style={{ textAlign: "center", marginBottom: space.xl }}>
      <Link to="/" style={{ display: "inline-block" }}>
        <img
          src="/brand-mark.png"
          alt="TickAndPass"
          style={{ height: 32, width: "auto", objectFit: "contain" }}
        />
      </Link>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  backgroundColor: C.background,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  fontFamily: font,
  color: C.textStrong,
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: type.label.fontSize,
  lineHeight: type.label.lineHeight,
  fontWeight: type.label.fontWeight,
  color: C.textMuted,
  marginBottom: space.sm, // 8px per brand manual
};

const inputStyle = {
  width: "100%",
  height: 48, // 48px field height per brand manual
  boxSizing: "border-box",
  padding: "0 16px",
  fontSize: type.body.fontSize,
  fontFamily: font,
  color: C.textStrong,
  backgroundColor: C.surface,
  border: `1.5px solid ${C.border}`,
  borderRadius: radii.control, // 8px
  outline: "none",
  transition: "border-color 0.15s ease",
};

const disabledInputStyle = {
  ...inputStyle,
  backgroundColor: C.surfaceSunken,
  color: C.textSubtle,
  cursor: "not-allowed",
  border: `1px solid ${C.divider}`,
};

const hintStyle = {
  fontSize: type.caption.fontSize,
  lineHeight: type.caption.lineHeight,
  color: C.textSubtle,
  marginTop: 4,
};
